import React, { useContext, useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import DocumentMeta from "react-document-meta";
import axios from "axios";
import ExpandableBox from "../Components/ExpandableBox";
import HeaderBanner from "../Components/HeaderBanner";
import FaqSideBar from "../Components/FaqSideBar";
import Config from "../config.json";
import Loading from "./Loading";
import ComponentContext from "../Context";
import FaqImage from "../Images/faq.png";
import Statics from "../Statics";

const slugify = text =>
  text
    ? text
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
    : "";

const page_meta = {
  language: "fa",
  copyright: "پژوهیار",
  description: "نرم‌افزار استناددهی",
  title: Config.title + " | پرسش‌های متداول",
  meta: {
    charset: "utf-8",
    name: {
      keywords: "پژوهیار",
    },
  },
};

const title = "پرسش‌های متداول";
const body =
  "صفحه پرسش‌های متداول شامل پرسش‌های پرتکرار کاربران، پاسخ و راهنمایی در بخش‌های مختلف نرم‌افزار پژوهیار است.";

const sortByPriority = (left, right) => {
  const leftOrder = Number(left?.sort_order ?? left?.priority ?? 0);
  const rightOrder = Number(right?.sort_order ?? right?.priority ?? 0);

  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }

  return 0;
};

const normalizeFaqCategories = rawCategories =>
  (Array.isArray(rawCategories) ? [...rawCategories] : [])
    .map((category, categoryIndex) => ({
      id: category?.id || `${categoryIndex + 1}`,
      value: category?.title || category?.value || "دسته‌بندی نشده",
      slug:
        category?.slug ||
        slugify(category?.title || category?.value || `faq-category-${categoryIndex + 1}`),
      sort_order:
        Number(category?.sort_order ?? category?.priority ?? categoryIndex + 1) ||
        categoryIndex + 1,
      questions: (Array.isArray(category?.questions) ? [...category.questions] : [])
        .map((question, questionIndex) => ({
          id: question?.id || `${categoryIndex + 1}-${questionIndex + 1}`,
          title: question?.title || question?.question || "پرسش بدون عنوان",
          body: question?.body || question?.answer || "",
          slug:
            question?.slug ||
            slugify(
              question?.title ||
                question?.question ||
                `faq-question-${categoryIndex + 1}-${questionIndex + 1}`,
            ),
          sort_order:
            Number(
              question?.sort_order ?? question?.priority ?? questionIndex + 1,
            ) || questionIndex + 1,
        }))
        .sort(sortByPriority),
    }))
    .sort(sortByPriority);

const Faq = () => {
  const context = useContext(ComponentContext);

  const [loading_state, set_loading_state] = useState(Statics.NOT_LOADED);
  const [questions, set_questions] = useState([]);
  const [selectedFaqCategory, setSelectedFaqCategory] = useState(null);
  const [activeQuestionSlug, setActiveQuestionSlug] = useState(null);

  const updateHash = (categorySlug, questionSlug) => {
    if (typeof window === "undefined") return;
    const { pathname, search } = window.location;
    if (categorySlug) {
      const params = new URLSearchParams();
      params.set("category", categorySlug);
      if (questionSlug) {
        params.set("question", questionSlug);
      }
      window.history.replaceState(
        null,
        "",
        `${pathname}${search}#${params.toString()}`,
      );
    } else {
      window.history.replaceState(null, "", `${pathname}${search}`);
    }
  };

  const parseHashParams = () => {
    if (typeof window === "undefined") return {};
    const hash = window.location.hash ? window.location.hash.substring(1) : "";
    const params = new URLSearchParams(hash);
    return {
      category: params.get("category"),
      question: params.get("question"),
    };
  };

  const handleCategorySelect = link => {
    setSelectedFaqCategory(link);
    setActiveQuestionSlug(null);
    updateHash(link?.slug || slugify(link?.value || ""), null);
  };

  const applyFaqPayload = categories => {
    const normalizedFaqs = normalizeFaqCategories(categories);
    set_questions(normalizedFaqs);
    setSelectedFaqCategory(currentCategory => {
      if (!normalizedFaqs.length) return null;
      if (currentCategory?.slug) {
        const matchedCurrent = normalizedFaqs.find(
          item => item.slug === currentCategory.slug,
        );
        if (matchedCurrent) {
          return matchedCurrent;
        }
      }

      return normalizedFaqs[0];
    });
    set_loading_state(Statics.LOADED);
  };

  const loadLegacyFaqFallback = () => {
    context.ApiDriver.getStaticValue(
      {
        slugs: ["faq_questions"],
      },
      data => {
        applyFaqPayload(data?.data?.[0]?.value || []);
      },
      error => {
        console.log(error);
        set_loading_state(Statics.CAUSED_ERROR);
      },
    );
  };

  function getQuestions() {
    set_loading_state(Statics.LOADING);
    axios
      .get(`${context.ApiDriver.config.url}/faq`, {
        params: { active: 1, _ts: Date.now() },
      })
      .then(({ data }) => {
        applyFaqPayload(data?.data || []);
      })
      .catch(error => {
        console.log(error);
        loadLegacyFaqFallback();
      });
  }

  useEffect(() => {
    if (loading_state === Statics.NOT_LOADED) {
      getQuestions();
    }
    // eslint-disable-next-line
  }, [loading_state]);

  useEffect(() => {
    if (loading_state !== Statics.LOADED || !questions.length) return;
    const { category, question } = parseHashParams();
    if (category) {
      const matchedCategory = questions.find(item => item.slug === category);
      if (matchedCategory) {
        setSelectedFaqCategory(matchedCategory);
        setActiveQuestionSlug(question);
        return;
      }
    }
    setSelectedFaqCategory(questions[0] || null);
    setActiveQuestionSlug(null);
    // eslint-disable-next-line
  }, [loading_state, questions]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleHashChange = () => {
      const { category, question } = parseHashParams();
      if (category) {
        const matchedCategory = questions.find(item => item.slug === category);
        if (matchedCategory) {
          setSelectedFaqCategory(matchedCategory);
          setActiveQuestionSlug(question);
          return;
        }
      }
      if (questions.length) {
        setSelectedFaqCategory(questions[0]);
      }
      setActiveQuestionSlug(null);
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
    // eslint-disable-next-line
  }, [questions]);

  const handleActiveQuestionChange = slug => {
    setActiveQuestionSlug(slug);
    const categorySlug =
      selectedFaqCategory?.slug || slugify(selectedFaqCategory?.value || "");
    if (categorySlug) {
      updateHash(categorySlug, slug || null);
    } else {
      updateHash();
    }
  };

  return (
    <DocumentMeta {...page_meta}>
      <HeaderBanner
        image={FaqImage}
        body={body}
        title={title}
        header_color="#eef0ff"
      />
      <div>
        <Row className="p-4 p-md-5 mx-0">
          {loading_state === Statics.LOADING ? (
            <div className="pajoohyar-loading">
              <Loading />
            </div>
          ) : null}
          {loading_state === Statics.CAUSED_ERROR ? (
            <div className="version-error-parent">
              <p>ارتباط با سرور با مشکل مواجه شد !</p>
              <img
                className="version-error-img"
                src={`${process.env.PUBLIC_URL}/images/errorPic.webp`}
                alt="error-png"
              />
            </div>
          ) : null}
          <Col md={3} className="mb-3 mb-md-0 p-0 pl-0 pl-md-3">
            {loading_state === Statics.LOADED ? (
              <FaqSideBar
                links={questions}
                onCategorySeclect={handleCategorySelect}
              />
            ) : null}
          </Col>

          {loading_state === Statics.LOADED && selectedFaqCategory ? (
            <Col md={9} className="container-content-with-sidebar">
              <h5 className="questions-title">{selectedFaqCategory.value}</h5>
              <div className="px-3">
                <ExpandableBox
                  data={selectedFaqCategory.questions}
                  initialActiveId={activeQuestionSlug}
                  onActiveChange={handleActiveQuestionChange}
                />
              </div>
            </Col>
          ) : null}
        </Row>
      </div>
    </DocumentMeta>
  );
};

export default Faq;
